import React from 'react';
import { Link } from 'react-router-dom';
import './rush.css';
import './legal.css';
import { RushNav, RushFooter, useRushReveal } from './RushChrome';
import { LegalPage } from './LegalPage';

const SECTIONS = [
  { id: 'agreement', title: 'The agreement', body: (
    <>
      <p>These Terms of Service ("Terms") are a contract between you (or the organization you represent) and <strong>Rush Studio Ltd.</strong>, a UK company registered at 12 Hatton Garden, London EC1N 8AT.</p>
      <p>By creating an account, signing an order form, or otherwise using rush.studio (the "Service"), you agree to these Terms. If you don't agree, don't use the Service.</p>
      <div className="legal-callout"><strong>If you have a signed agreement with us</strong> (an MSA, DPA, or order form), that agreement takes precedence over these Terms where they conflict.</div>
    </>
  ) },
  { id: 'service', title: 'What the service does', body: (
    <>
      <p>Rush Studio is a translation workspace. You upload source content, we route it through AI and (where you choose) human reviewers, you get translations back. We provide project management, translation memory, glossaries, integrations, and an API for all of the above.</p>
      <p>The Service is provided "as is" — we work hard to make it good, but we don't promise it'll be perfect or available 100% of the time. We commit to specific uptime targets in our enterprise SLAs; outside those, we aim for the same standard but don't legally guarantee it.</p>
    </>
  ) },
  { id: 'accounts', title: 'Your account', body: (
    <>
      <p>You're responsible for what happens under your account, including keeping your password secure. Tell us right away if you think someone else has access — email <a href="mailto:security@rushstudio.com">security@rushstudio.com</a>.</p>
      <p>You must be at least 16 to use Rush Studio. You must use a real name and a working email. You may not share accounts; each team member needs their own seat.</p>
    </>
  ) },
  { id: 'your-content', title: 'Your content', body: (
    <>
      <p><strong>You own your content.</strong> Uploading content to Rush Studio doesn't transfer ownership to us. You grant us a limited license to process and store it for the sole purpose of providing the Service.</p>
      <p>You're responsible for making sure you have the right to translate the content you upload — copyright, confidentiality, anything else. If you upload something you shouldn't, we may remove it on legal request.</p>
      <p>Translation memory, glossaries, and any other derivative artifacts of your content remain yours. You can export them at any time in standard formats (TMX, CSV, XLIFF).</p>
    </>
  ) },
  { id: 'acceptable-use', title: 'Acceptable use', body: (
    <>
      <p>Don't use Rush Studio to:</p>
      <ul>
        <li>Translate content you don't have the right to translate.</li>
        <li>Process material that's illegal where you are or where it'll be published.</li>
        <li>Send malware, phishing content, or anything designed to deceive recipients.</li>
        <li>Reverse-engineer the Service, scrape it at scale, or rebuild parts of it for resale.</li>
        <li>Try to break, bypass, or stress-test our security or rate limits without prior written agreement.</li>
      </ul>
      <p>If you do these things, we may suspend your account immediately. We'll tell you what happened and give you a chance to respond, except where the law or the safety of other users requires faster action.</p>
    </>
  ) },
  { id: 'ai-output', title: 'AI-generated translations', body: (
    <>
      <p>Rush Studio uses third-party AI engines for machine translation drafts. AI translations are <strong>not guaranteed accurate</strong>. They're a starting point — you're responsible for reviewing and approving anything you publish.</p>
      <p>We disclaim liability for AI outputs to the extent the law allows. If you need legal-grade translation, choose a plan that includes native human review, and make sure your reviewer signs off before you publish.</p>
    </>
  ) },
  { id: 'payment', title: 'Payment & billing', body: (
    <>
      <p>If you're on a paid plan, fees are listed on our <Link to="/pricing">pricing page</Link> or in your order form. You'll be billed in advance for each billing cycle (monthly or annual). All fees are exclusive of VAT/sales tax unless stated.</p>
      <p>Plans renew automatically. You can cancel at any time from your settings — your access continues until the end of the paid period, and we don't refund partial periods (except the 14-day money-back guarantee on new accounts).</p>
      <p>If a payment fails, we'll retry a few times and notify you. After 14 days of non-payment, we may suspend the account. Your data is preserved for 30 days after suspension before deletion.</p>
    </>
  ) },
  { id: 'ip', title: 'Intellectual property', body: (
    <>
      <p>Rush Studio's software, design, brand, and documentation are ours. You don't get any rights to them beyond the limited license to use the Service that we grant you in these Terms.</p>
      <p>Anything you build using our API or webhooks — your own integrations, scripts, dashboards — is yours.</p>
      <p>If you give us feedback, suggestions, or feature requests, we may use them without owing you anything for them. Don't send us confidential ideas you want to keep.</p>
    </>
  ) },
  { id: 'termination', title: 'Termination', body: (
    <>
      <p>You can close your account anytime from settings. We'll delete your content within 30 days, and we can give you an export before then.</p>
      <p>We can terminate your account if you breach these Terms, if your payment is significantly overdue, or if we're legally required to. We'll give 30 days' notice except in cases of egregious breach or security risk.</p>
      <p>Sections of these Terms that should reasonably survive termination — IP, indemnity, limitation of liability, governing law — do.</p>
    </>
  ) },
  { id: 'warranties', title: 'Warranties & disclaimers', body: (
    <>
      <p>To the extent permitted by law, the Service is provided "as is" and "as available" without warranties of any kind, express or implied. We don't warrant that it will be uninterrupted, error-free, or that translations will be accurate.</p>
      <p>Nothing in these Terms excludes liability for fraud, death, or personal injury caused by our negligence — laws prohibit that exclusion and we wouldn't want to anyway.</p>
    </>
  ) },
  { id: 'liability', title: 'Limitation of liability', body: (
    <>
      <p>To the maximum extent permitted by law, our total liability under these Terms is capped at the greater of (a) the amount you've paid us in the 12 months before the claim, or (b) £100.</p>
      <p>We're not liable for indirect, consequential, special, or punitive damages — lost profits, lost business, loss of data beyond what's recoverable from our backups, and similar.</p>
      <p>This cap doesn't apply to liability that can't be limited under applicable law.</p>
    </>
  ) },
  { id: 'changes', title: 'Changes to these terms', body: (
    <>
      <p>We may update these Terms occasionally. For material changes — anything that affects your rights or obligations — we'll email you at least 30 days before they take effect. Continuing to use the Service after the effective date means you accept the new Terms.</p>
      <p>Cosmetic changes (typo fixes, broken links, clarifications that don't change meaning) we make silently. A full revision log is at <a href="#">rush.studio/terms/changelog</a>.</p>
    </>
  ) },
  { id: 'governing-law', title: 'Governing law & disputes', body: (
    <>
      <p>These Terms are governed by the laws of <strong>England and Wales</strong>. Disputes go to the courts of London, except where consumer protection law in your country gives you a non-waivable right to use your local courts.</p>
      <p>Before anything ends up in court, please email <a href="mailto:legal@rushstudio.com">legal@rushstudio.com</a> — most things resolve in a conversation.</p>
    </>
  ) },
  { id: 'contact', title: 'Contact', body: (
    <>
      <p>Questions about these Terms? <a href="mailto:legal@rushstudio.com">legal@rushstudio.com</a> or write to us:</p>
      <p>Rush Studio Ltd.<br />12 Hatton Garden<br />London EC1N 8AT<br />United Kingdom</p>
      <p>For privacy questions specifically, see our <Link to="/privacy">Privacy Policy</Link> or <Link to="/gdpr">GDPR page</Link>.</p>
    </>
  ) },
];

const RushTermsPage = () => {
  useRushReveal();
  return (
    <div className="rs">
      <RushNav />
      <LegalPage
        eyebrow="Legal · Terms"
        title="Terms of Service"
        updated="May 20, 2026"
        intro="The rules of using Rush Studio. We've kept them as short and plain as we could without giving our lawyers heartburn."
        sections={SECTIONS}
        related={[
          { href: '/privacy', label: 'Privacy Policy' },
          { href: '/gdpr', label: 'GDPR & DPA' },
          { href: '/contact', label: 'Contact us' },
        ]}
      />
      <RushFooter />
    </div>
  );
};

export default RushTermsPage;
